import { createContext, useContext } from "react";
import {
  arrayOf,
  bool,
  func,
  number,
  object,
  objectOf,
  shape,
  string,
} from "prop-types";
import { markerTypes } from "./annotationCommonPropTypes.js";
import { useAnnotationState, blankState } from "./annotationState.js";

export const AnnotationContext = createContext();

AnnotationContext.Provider.propTypes = {
  value: shape({
    data: shape({
      criteriaUrl: string,
      annotationEndPointUrl: string.isRequired,
      userIsLoggedIn: bool.isRequired,
      userEmailAddress: string,
      itemLink: string.isRequired,
      sitecoreItemId: string.isRequired,
      selectedContentImage: string,
      annotations: arrayOf(
        shape({
          authorName: string.isRequired,
          authorOrganization: string,
          body: string.isRequired,
          replies: arrayOf(
            shape({
              authorName: string.isRequired,
              authorOrganization: string,
              body: string.isRequired,
            }),
          ).isRequired,
        }),
      ).isRequired,
      content: shape({
        annotations: string.isRequired,
        closeModalLabel: string.isRequired,
        login: string.isRequired,
        signUp: string.isRequired,
        replyTo: string.isRequired,
        reply: string.isRequired,
        criteriaLink: string.isRequired,
        unauthenticatedTitle: string.isRequired,
        unauthenticatedMessage: string.isRequired,
        authenticatedMessage: string.isRequired,
        successTitle: string.isRequired,
        successMessage: string.isRequired,
        successAction: string.isRequired,
        submitButtonText: string.isRequired,
        cancelButtonText: string.isRequired,
        serverError: string.isRequired,
        inputError: string.isRequired,
        selectedContentLabel: string.isRequired,
        selectedContentMessage: string,
        createNewAnnotationText: string.isRequired,
        viewAddAnnotationText: string.isRequired,
        responsePlaceholderUnauthenticated: string.isRequired,
      }).isRequired,
    }).isRequired,
    marker: shape({
      type: markerTypes.isRequired, // see ./annotationCommonPropTypes
      positionTop: number,
      positionRight: number,
      noColor: bool,
    }).isRequired,
    state: shape({
      isModalOpen: bool.isRequired,
      isSelectionActive: bool.isRequired,
      isOverlayActive: bool.isRequired,
    }).isRequired,
    actions: objectOf(func).isRequired,
  }),
};

/**
 * AnnotationContextProvider, has the value built-in
 * usage:
 *   <AnnotationContextProvider> ... </AnnotationContextProvider>
 */
export const AnnotationContextProvider = (props) => {
  const {
    markerType,
    markerPositionTop,
    markerPositionRight,
    markerNoColor,
    data,
    ...otherProps
  } = props;

  const initialState = {
    ...blankState,
  };

  const [state, actions] = useAnnotationState(initialState);

  const context = {
    marker: {
      markerType: markerType,
      markerPositionTop: markerPositionTop,
      markerPositionRight: markerPositionRight,
      markerNoColor: markerNoColor,
    },
    data,
    state,
    actions,
  };

  return <AnnotationContext.Provider value={context} {...otherProps} />;
};

AnnotationContextProvider.propTypes = {
  markerType: markerTypes,
  markerPositionTop: number,
  markerPositionRight: number,
  markerNoColor: bool,
  data: object.isRequired,
};

/**
 * useAnnotationContext
 * usage:
 *   const {
 *     state: { path },
 *     actions: { setPath }
 *   } = useAnnotationContext();
 */
export function useAnnotationContext() {
  return useContext(AnnotationContext);
}
